import * as Yup from "yup";
import { getIn, setIn } from "formik";
import { toast } from "react-toastify";

function keep(pattern) {
  return function (value) {
    return value.replace(pattern, "");
  };
}

function upper(filter) {
  return function (value) {
    return filter(value.toUpperCase());
  };
}

function collapseSpaces(filter) {
  return function (value) {
    return filter(value).replace(/^\s+/, "").replace(/\s{2,}/g, " ");
  };
}

function decimalFilter(maxInt, maxDecimals) {
  return function (value) {
    var cleaned = value.replace(/[^0-9.]/g, "");
    var dot = cleaned.indexOf(".");
    var intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
    intPart = intPart.slice(0, maxInt);
    if (dot === -1) {
      return intPart;
    }
    var decPart = cleaned
      .slice(dot + 1)
      .replace(/\./g, "")
      .slice(0, maxDecimals);
    return intPart + "." + decPart;
  };
}

var digits = keep(/\D/g);

var PERSON_NAME_RE = /^[A-Za-z]+(?:[ .'-]+[A-Za-z]+)*\.?$/;
var PLACE_RE = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
var EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
var PF_PLAIN_RE = /^[A-Z]{5}\d{17}$/;
var PF_SLASH_RE = /^[A-Z]{2}\/[A-Z]{3}\/\d{1,7}\/\d{1,3}\/\d{1,7}$/;
var LINKEDIN_RE =
  /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/(in|pub|company)\/[A-Za-z0-9_\-%.]{2,100}\/?$/i;
var GRADE_RE =
  /^(O|A\+{0,2}|A-|B\+{0,1}|B-|C\+{0,1}|C-|D|E|P|PASS|FIRST CLASS|DISTINCTION)$/i;
var IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
var ICICI_IFSC_RE = /^ICIC0[A-Z0-9]{6}$/;

function lengthCheck(min, max, label) {
  return function (value) {
    if (min && value.length < min) {
      return label + " must be at least " + min + " characters";
    }
    if (max && value.length > max) {
      return label + " must be at most " + max + " characters";
    }
    return null;
  };
}

function numberInRange(min, max, label) {
  return function (value) {
    if (!/^\d+(\.\d+)?$/.test(value)) {
      return "Enter a valid number";
    }
    var n = parseFloat(value);
    if (n < min || n > max) {
      return label + " must be between " + min + " and " + max;
    }
    return null;
  };
}

/**
 * Each rule: filter (applied while typing), maxLength, inputMode,
 * placeholder (example value), check(value, label) -> error | null.
 * check only runs on non-empty, trimmed values; "required" is handled by ruleSchema.
 */
export var INPUT_RULES = {
  personName: {
    maxLength: 50,
    placeholder: "e.g. Rahul Sharma",
    filter: collapseSpaces(keep(/[^A-Za-z .'-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 50, label)(value) ||
        (PERSON_NAME_RE.test(value)
          ? null
          : label + " can contain only letters, spaces, . ' and -")
      );
    },
  },
  email: {
    maxLength: 254,
    inputMode: "email",
    type: "email",
    placeholder: "e.g. name@example.com",
    filter: keep(/[^A-Za-z0-9._%+\-@]/g),
    check: function (value) {
      return EMAIL_RE.test(value) && value.length <= 254
        ? null
        : "Enter a valid email address";
    },
  },
  mobile: {
    maxLength: 10,
    nativeMaxLength: false,
    inputMode: "numeric",
    type: "tel",
    placeholder: "e.g. 9876543210",
    filter: function (value) {
      var d = digits(value);
      if (d.length === 12 && d.indexOf("91") === 0) {
        return d.slice(2);
      }
      if (d.length === 11 && d.charAt(0) === "0") {
        return d.slice(1);
      }
      return d.slice(0, 10);
    },
    check: function (value) {
      return /^[6-9]\d{9}$/.test(value)
        ? null
        : "Enter a valid 10-digit mobile number starting with 6-9";
    },
  },
  postalCode: {
    maxLength: 6,
    inputMode: "numeric",
    placeholder: "e.g. 411001",
    filter: digits,
    check: function (value) {
      return /^[1-9]\d{5}$/.test(value)
        ? null
        : "Enter a valid 6-digit postal code";
    },
  },
  years: {
    maxLength: 2,
    inputMode: "numeric",
    placeholder: "e.g. 2",
    filter: digits,
    check: function (value, label) {
      return numberInRange(0, 99, label)(value);
    },
  },
  pf: {
    maxLength: 26,
    placeholder: "e.g. MHBAN00000640000000125",
    filter: upper(keep(/[^A-Z0-9/]/g)),
    check: function (value, label) {
      return PF_PLAIN_RE.test(value) || PF_SLASH_RE.test(value)
        ? null
        : label +
            " must be like MHBAN00000640000000125 or MH/BAN/0000064/000/0000125";
    },
  },
  uan: {
    maxLength: 12,
    inputMode: "numeric",
    placeholder: "e.g. 100123456789",
    filter: digits,
    check: function (value) {
      return /^\d{12}$/.test(value) ? null : "UAN must be exactly 12 digits";
    },
  },
  esic: {
    maxLength: 10,
    inputMode: "numeric",
    placeholder: "e.g. 3100123456",
    filter: digits,
    check: function (value) {
      return /^\d{10}$/.test(value)
        ? null
        : "ESIC number must be exactly 10 digits";
    },
  },
  linkedIn: {
    maxLength: 200,
    inputMode: "url",
    type: "url",
    placeholder: "e.g. https://www.linkedin.com/in/your-name",
    filter: keep(/\s/g),
    check: function (value) {
      return LINKEDIN_RE.test(value)
        ? null
        : "Enter a valid LinkedIn URL, e.g. https://www.linkedin.com/in/your-name";
    },
  },
  remuneration: {
    maxLength: 6,
    inputMode: "decimal",
    placeholder: "e.g. 12.50",
    filter: decimalFilter(3, 2),
    check: function (value, label) {
      if (!/^\d{1,3}(\.\d{1,2})?$/.test(value)) {
        return "Enter up to 2 decimals, e.g. 12.50";
      }
      var n = parseFloat(value);
      return n > 0 && n <= 999.99
        ? null
        : label + " must be between 0.01 and 999.99";
    },
  },
  percentShare: {
    maxLength: 6,
    inputMode: "decimal",
    placeholder: "e.g. 100",
    filter: decimalFilter(3, 2),
    check: function (value, label) {
      return numberInRange(1, 100, label)(value);
    },
  },
  result: {
    maxLength: 11,
    placeholder: "e.g. 80, 8.5 or A+",
    filter: collapseSpaces(upper(keep(/[^A-Z0-9.+\-% ]/g))),
    check: function (value) {
      var numeric = value.replace(/\s*%$/, "");
      if (/^\d+(\.\d{1,2})?$/.test(numeric)) {
        var n = parseFloat(numeric);
        return n >= 0 && n <= 100
          ? null
          : "Enter a percentage (0-100) or CGPA (0-10)";
      }
      return GRADE_RE.test(value)
        ? null
        : "Enter a percentage (80), CGPA (8.5) or grade (A+, Pass)";
    },
  },
  address: {
    maxLength: 150,
    placeholder: "e.g. Flat 12, Shanti Apartments, MG Road",
    filter: collapseSpaces(keep(/[^A-Za-z0-9 ,./#\-()&\n]/g)),
    check: function (value, label) {
      return (
        lengthCheck(3, 150, label)(value) ||
        (/[A-Za-z0-9]/.test(value) ? null : "Enter a valid " + label.toLowerCase())
      );
    },
  },
  place: {
    maxLength: 50,
    placeholder: "e.g. Pune",
    filter: collapseSpaces(keep(/[^A-Za-z ]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 50, label)(value) ||
        (PLACE_RE.test(value) ? null : label + " can contain only letters")
      );
    },
  },
  text: {
    maxLength: 60,
    filter: collapseSpaces(keep(/[^A-Za-z0-9 &.,()/'-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 60, label)(value) ||
        (/[A-Za-z]/.test(value) ? null : "Enter a valid " + label.toLowerCase())
      );
    },
  },
  passport: {
    maxLength: 8,
    placeholder: "e.g. A1234567",
    filter: function (value) {
      var out = "";
      value
        .toUpperCase()
        .split("")
        .forEach(function (ch) {
          if (out.length === 0 ? /[A-Z]/.test(ch) : /[0-9]/.test(ch)) {
            out += ch;
          }
        });
      return out;
    },
    check: function (value) {
      return /^[A-Z][0-9]{7}$/.test(value)
        ? null
        : "Passport number must be 1 letter followed by 7 digits, e.g. A1234567";
    },
  },
  occupation: {
    maxLength: 50,
    placeholder: "e.g. Teacher",
    filter: collapseSpaces(keep(/[^A-Za-z &./()-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 50, label)(value) ||
        (/^[A-Za-z][A-Za-z &./()-]*$/.test(value)
          ? null
          : label + " can contain only letters")
      );
    },
  },
  orgName: {
    maxLength: 100,
    placeholder: "e.g. Infosys Limited",
    filter: collapseSpaces(keep(/[^A-Za-z0-9 &.,()/'-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 100, label)(value) ||
        (/[A-Za-z]/.test(value) ? null : "Enter a valid " + label.toLowerCase())
      );
    },
  },
  code: {
    maxLength: 20,
    filter: upper(keep(/[^A-Z0-9/-]/g)),
    check: function (value, label) {
      return /^[A-Z0-9][A-Z0-9/-]*$/.test(value) && value.length <= 20
        ? null
        : label + " can contain only letters, digits, / and -";
    },
  },
  longCode: {
    maxLength: 30,
    filter: upper(keep(/[^A-Z0-9/-]/g)),
    check: function (value, label) {
      return /^[A-Z0-9][A-Z0-9/-]*$/.test(value) && value.length <= 30
        ? null
        : label + " can contain only letters, digits, / and -";
    },
  },
  skill: {
    maxLength: 50,
    placeholder: "e.g. React.js",
    filter: collapseSpaces(keep(/[^A-Za-z0-9 +#./&-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(1, 50, label)(value) ||
        (/[A-Za-z0-9]/.test(value) ? null : "Enter a valid skill")
      );
    },
  },
  accountNumber: {
    maxLength: 18,
    inputMode: "numeric",
    placeholder: "e.g. 123456789012",
    filter: digits,
    check: function (value) {
      return /^\d{9,18}$/.test(value)
        ? null
        : "Account number must be 9 to 18 digits";
    },
  },
  iciciAccountNumber: {
    maxLength: 12,
    inputMode: "numeric",
    placeholder: "e.g. 123456789012",
    filter: digits,
    check: function (value) {
      return /^\d{12}$/.test(value)
        ? null
        : "ICICI account number must be exactly 12 digits";
    },
  },
  ifsc: {
    maxLength: 11,
    placeholder: "e.g. SBIN0001234",
    filter: upper(keep(/[^A-Z0-9]/g)),
    check: function (value) {
      return IFSC_RE.test(value)
        ? null
        : "Enter a valid 11-character IFSC, e.g. SBIN0001234";
    },
  },
  iciciIfsc: {
    maxLength: 11,
    placeholder: "e.g. ICIC0001234",
    filter: upper(keep(/[^A-Z0-9]/g)),
    check: function (value) {
      return ICICI_IFSC_RE.test(value)
        ? null
        : "Enter a valid ICICI IFSC, e.g. ICIC0001234";
    },
  },
  bankText: {
    maxLength: 60,
    filter: collapseSpaces(keep(/[^A-Za-z0-9 &.,()-]/g)),
    check: function (value, label) {
      return (
        lengthCheck(2, 60, label)(value) ||
        (/[A-Za-z]/.test(value) ? null : "Enter a valid " + label.toLowerCase())
      );
    },
  },
};

export function getInputRule(key) {
  return key ? INPUT_RULES[key] || null : null;
}

export function applyInputRule(key, value) {
  var rule = getInputRule(key);
  var str = value == null ? "" : String(value);
  if (!rule) {
    return str;
  }
  var next = rule.filter ? rule.filter(str) : str;
  if (rule.maxLength && next.length > rule.maxLength) {
    next = next.slice(0, rule.maxLength);
  }
  return next;
}

export function checkInputRule(key, value, label) {
  var rule = getInputRule(key);
  var str = value == null ? "" : String(value).trim();
  if (!rule || !str || !rule.check) {
    return null;
  }
  return rule.check(str, label || "This field");
}

/**
 * Yup string schema for a rule. Empty values pass unless `required`.
 * options: { label, required, requiredMessage }
 */
export function ruleSchema(key, options) {
  var opts = options || {};
  var label = opts.label || "This field";
  var schema = Yup.string().nullable();
  if (opts.required) {
    schema = schema
      .trim()
      .required(opts.requiredMessage || label + " is required");
  }
  return schema.test("format-" + key, label + " is invalid", function (value) {
    var message = checkInputRule(key, value, label);
    return message ? this.createError({ message: message }) : true;
  });
}

function isEmptyValue(value) {
  if (value == null || value === false) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

/**
 * Draft check: only format errors on filled, single-value fields.
 * Skips "required", empty values and list/object-level rules (e.g. share totals).
 */
export function collectDraftErrors(schema, values) {
  return schema
    .validate(values, { abortEarly: false })
    .then(function () {
      return { errors: {}, paths: [] };
    })
    .catch(function (err) {
      var errors = {};
      var paths = [];
      var list = err && err.inner && err.inner.length ? err.inner : [err];
      list.forEach(function (item) {
        if (!item || !item.path || item.type === "required") {
          return;
        }
        var value = getIn(values, item.path);
        if (isEmptyValue(value) || (value !== null && typeof value === "object")) {
          return;
        }
        if (getIn(errors, item.path) === undefined) {
          errors = setIn(errors, item.path, item.message);
          paths.push(item.path);
        }
      });
      return { errors: errors, paths: paths };
    });
}

export var FORM_INVALID_MESSAGE =
  "Please fill in all required fields correctly before saving.";

/** Shows one standard toast when a submit is blocked by validation errors. */
export function notifyFormInvalid(errors) {
  if (!errors || Object.keys(errors).length === 0) {
    return false;
  }
  toast.error(FORM_INVALID_MESSAGE, { toastId: "form-invalid" });
  return true;
}

/** Resolves true when the draft may be saved; otherwise marks the invalid fields. */
export function validateDraft(formik, schema) {
  if (!formik || !schema) {
    return Promise.resolve(true);
  }
  return collectDraftErrors(schema, formik.values).then(function (result) {
    if (result.paths.length === 0) {
      return true;
    }
    result.paths.forEach(function (path) {
      formik.setFieldTouched(path, true, false);
    });
    formik.setErrors(Object.assign({}, formik.errors, result.errors));
    toast.error("Please correct the highlighted fields before saving the draft.", {
      toastId: "draft-invalid",
    });
    return false;
  });
}
